import { EntityReducers } from '@core/store/EntityReducers';
import {
  ExtendedBaseState,
  getBaseState,
  getEntityAdapterReducers,
  handleFulfilledAction,
  handleMultipleLoadingActions,
} from '@hwm/ui-lib';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { thunks } from './PreferencesThunks';
import { PreferencesModel } from '@models/Preferences';
import { defaultPreferencesValues } from '@shared/components/Preferences/Preferences.utils';
import { actionInterceptor } from '@core/store/middleware/actionsInterceptorMiddleware';
import { DefaultMapFilters, MapFilters, setMapFilters } from '../../slices/map/map.reducer';

interface PreferencesStoreModel extends PreferencesModel {
  id: number;
}

interface PreferencesStateModel
  extends ExtendedBaseState<PreferencesStoreModel, number, { userPreferences: PreferencesModel }> {}

const entityAdapter = createEntityAdapter<PreferencesStoreModel>({});

const slice = createSlice({
  name: EntityReducers.preferences,
  initialState: {
    ...getBaseState(entityAdapter, {
      userPreferences: defaultPreferencesValues,
    }),
  },
  reducers: {
    ...getEntityAdapterReducers(entityAdapter),
  },
  extraReducers: (builder) => {
    handleMultipleLoadingActions(
      [
        thunks.getUserPreferences,
        thunks.saveUserPreferences,
        thunks.getAccountPreferences,
        thunks.saveAccountPreferences,
      ],
      builder
    );
    handleFulfilledAction(thunks.getUserPreferences, builder, (state: PreferencesStateModel, payload) => {
      state.userPreferences = payload;
    });
    handleFulfilledAction(thunks.saveUserPreferences, builder, (state: PreferencesStateModel, payload) => {
      state.userPreferences = payload.data;
    });
    handleFulfilledAction(thunks.getAccountPreferences, builder, (state: PreferencesStateModel, payload) => {
      const { accountId, ...data } = payload;
      entityAdapter.upsertOne(state, { id: accountId, ...data });
    });
    handleFulfilledAction(thunks.saveAccountPreferences, builder, (state: PreferencesStateModel, payload) => {
      entityAdapter.updateOne(state, {
        id: payload.accountId,
        changes: payload.data,
      });
    });
  },
});

actionInterceptor.addActionsCallback<typeof thunks.getUserPreferences.fulfilled>({
  actionTypes: [thunks.getUserPreferences.fulfilled.type],
  callback: ({ action: { payload }, dispatch }) => {
    const mapFilersKeys = Object.keys(DefaultMapFilters);

    const data = Object.fromEntries(
      Object.entries(payload).filter(([key]) => mapFilersKeys.some((k) => k === key))
    ) as MapFilters;

    dispatch(setMapFilters(data));
  },
});
actionInterceptor.addActionsCallback<typeof thunks.saveUserPreferences.fulfilled>({
  actionTypes: [thunks.saveUserPreferences.fulfilled.type],
  callback: ({ action: { payload }, dispatch }) => {
    const mapFilersKeys = Object.keys(DefaultMapFilters);

    const data = Object.fromEntries(
      Object.entries(payload.data).filter(([key]) => mapFilersKeys.some((k) => k === key))
    ) as MapFilters;

    dispatch(setMapFilters(data));
  },
});
actionInterceptor.addActionsCallback<typeof thunks.saveAccountPreferences.fulfilled>({
  actionTypes: [thunks.saveAccountPreferences.fulfilled.type],
  callback: ({ dispatch }) => {
    dispatch(thunks.getUserPreferences());
  },
});

export const preferencesReducer = {
  slice,
  entityAdapter,
  thunks,
};
